import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React from "react";
import { useSWRConfig } from "swr";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { CustomerFormData } from "@/components/screens/CustomerFormPage/types";
import { COMPANY_PERMISSIONS } from "@/constants/modules.permissions";
import SWR_KEYS from "@/constants/swr-keys";
import useCreateCustomer from "@/hooks/requests/customer/use-create-customer";
import useUpdateCustomer from "@/hooks/requests/customer/use-update-customer";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useMutate from "@/hooks/use-mutate";
import ICustomer from "@/interfaces/customer.interfaces";
import { CustomerFormMessages } from "@/messages/customer/customer-form.messages";

import AddressFields from "./components/addressFields";
import GeneralInfo from "./components/generalInfo";
import LogoImage from "./components/logoImage";
import {
  buildFormSchema,
  buildInitialData,
  normalizeCreateData,
  normalizeUpdateData,
} from "./form-data";

type Props = {
  messages: CustomerFormMessages;
  customer: ICustomer | null;
  isLoading?: boolean;
};

function GeneralInformation({ messages, customer, isLoading: externalLoading }: Props) {
  const { update, isLoading: isLoadingUpdate } = useUpdateCustomer();
  const { create, isLoading: isLoadingCreate } = useCreateCustomer();
  const { mutate: globalMutate } = useSWRConfig();
  const { mutate } = useMutate();

  const hasNoPermissionToEdit = useHasNoPermission(COMPANY_PERMISSIONS.EDIT);
  const isEditing = !!customer;
  const isLoading = isLoadingUpdate || isLoadingCreate || externalLoading;

  const onSuccess = () => {
    // Invalidate all customer-related cache entries
    mutate(SWR_KEYS.getCustomerById);
    // Also invalidate the specific customer cache entry
    globalMutate([SWR_KEYS.getCustomerById, Number(customer?.id)]);
  };

  const handleUpdate = async (_formData: CustomerFormData) => {
    if (!customer) return;
    const formData = normalizeUpdateData(_formData);
    const successMessage = messages.updateCustomerSuccess;
    const errorMessage = messages.updateCustomerError;
    const _id = Number(customer.id) || 0;
    await update({ _id, formData, successMessage, errorMessage, onSuccess });
  };

  const handleCreate = async (_formData: CustomerFormData) => {
    const formData = normalizeCreateData(_formData);
    const successMessage = messages.createCustomerSuccess;
    const errorMessage = messages.createCustomerError;
    await create({ formData, successMessage, errorMessage, onSuccess });
  };

  const handleSubmit = async (_formData: CustomerFormData) => {
    if (isEditing) {
      await handleUpdate(_formData);
    } else {
      await handleCreate(_formData);
    }
  };

  return (
    <Formik
      initialValues={buildInitialData(customer)}
      validationSchema={buildFormSchema(messages)}
      validateOnChange={false}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {({ submitForm }) => {
        return (
          <MDBox mt={4}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 4 }}>
                <MDBox height="100%">
                  <LogoImage messages={messages} />
                </MDBox>
              </Grid>
              <Grid size={{ xs: 12, lg: 8 }}>
                <MDBox height="100%">
                  <GeneralInfo messages={messages} />
                </MDBox>
              </Grid>

              <Grid size={{ xs: 12, lg: 12 }}>
                <AddressFields messages={messages} />
              </Grid>
            </Grid>

            <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <MDButton
                variant="gradient"
                color="dark"
                onClick={() => {
                  submitForm();
                }}
                isLoading={isLoading}
                disabled={hasNoPermissionToEdit}
              >
                {isEditing ? messages.updateButton : messages.createCustomerButton}
              </MDButton>
            </MDBox>
          </MDBox>
        );
      }}
    </Formik>
  );
}

export default GeneralInformation;
